import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router, UrlSerializer } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MedicalCenter, MedicalGuide, Speciality, Subespecialidade, UrlServicePath } from 'src/app/shared/models/api';
import { AppState } from 'src/app/shared/models/state';
import { ApiService } from 'src/app/shared/services/api.service';
import { NavService } from 'src/app/shared/services/nav.service';
import { StateService } from 'src/app/shared/services/state.service';
import { GoogleMapService } from '../google-map.service';
import { UrlService } from '../url.service';
import { UtilsService } from '../utils.service';
import { TitleCasePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class DoctorCentersHelperService {

  constructor(
    private apiService: ApiService,
    private url: UrlService,
    private utils: UtilsService,
    private stateService: StateService,
    private googleMapService: GoogleMapService,
    private nav: NavService,
    private router: Router,
    private serializer: UrlSerializer,
    private titleCasePipe: TitleCasePipe
  ) {

  }

  getSpecialities(state: Partial<AppState>): void {
    if (state?.specialities) return;
    this.apiService.getSpecialities(state)
      .subscribe(specialities => {
        this.stateService.updateState({
          ...state,
          specialities
        });
      });
  }

  getMedicalCentersAndDoctors(
    state: Partial<AppState>,
    navigate = false,
    onlyCenter = false
  ): void {

    const promises = [
      this.apiService.getMedicalCenters(state,
        state.searchEngineType === 'diagnostic-test' ? 'pruebasDiagnosticas' : null),
      this.apiService.getMedicalDoctors(state)
    ];

    if (onlyCenter) promises.splice(1, 1);

    forkJoin(promises).subscribe(
      (res) => {

        if (!res[0]) {
          res[0] = {
            centrosMedicos: [],
            numeroElementos: '0',
            totalElementos: '0',
          };
        }

        if (!res[1]) {
          res[1] = {
            facultativos: [],
            numeroElementos: '0',
            totalElementos: '0',
          };
        }

        this.stateService.updateState({
          ...state,
          medicalCentersResponse: res[0],
          medicalDoctorsResponse: res[1]
        });

        const totalElementos = Number(res[0]?.totalElementos) + Number(res[1]?.totalElementos);

        if (navigate) {
          this.nav.navigateWithBackButton('search-results',
            { headerTitle: `${totalElementos} Resultados` });
        }
      }
    );
  }

  getAdditionalServices(
    state: Partial<AppState>,
    navigate = false) {

    this.apiService.getAditionalServices({
      ...state,
      centersPage: 0
    }).subscribe(
      (res) => {

        if (!res) {
          res = {
            centrosMedicos: [],
            numeroElementos: '0',
            totalElementos: '0',
          };
        }

        this.stateService.updateState({
          ...state,
          tabIndex: 0,
          centersPage: 0,
          doctorsPage: 0,
          hideSpecialistsTab: true,
          hideCentersTab: false,
          medicalCentersResponse: res,
        });

        if (navigate) {
          this.nav.navigateWithBackButton('search-results',
            { headerTitle: `${res.totalElementos} Resultados` });
        }
      }
    );

  }

  getMoreMedicalCenters(state: Partial<AppState>, urlPath?: UrlServicePath, navigate?: () => void): void {
    this.apiService.getMedicalCenters(state, urlPath).subscribe(res => {
      if (!res) return;
      const medicalCenterState = state?.medicalCentersResponse?.centrosMedicos || [];
      res.centrosMedicos = [
        ...medicalCenterState,
        ...res.centrosMedicos
      ];


      this.stateService.updateState({ ...state, medicalCentersResponse: res });
      if (navigate) navigate();
    });
  }

  getMoreMedicalDoctors(state: Partial<AppState>): void {
    this.apiService.getMedicalDoctors(state).subscribe(res => {
      if (!res) return;

      const data = state?.medicalDoctorsResponse.facultativos || [];

      res.facultativos = [
        ...data,
        ...res.facultativos
      ];

      this.stateService.updateState({ ...state, medicalDoctorsResponse: res });
    });
  }

  getMoreAdditionalServices(state: Partial<AppState>): void {
    this.apiService.getAditionalServices(state).subscribe(
      (res) => {
        if (!res) return;

        const medicalCenterState = state?.medicalCentersResponse?.centrosMedicos || [];
        res.centrosMedicos = [
          ...medicalCenterState,
          ...res.centrosMedicos
        ];

        this.stateService.updateState({ ...state, medicalCentersResponse: res });
      }
    );
  }

  getCompleteAddress(dataMoreInfo) {
    const street = `${dataMoreInfo.descripcionTipoVia} ${this.titleCasePipe.transform(dataMoreInfo.nombreVia)}`;
    const description = this.titleCasePipe.transform(dataMoreInfo.descripcionPoblacion);
    let addressList = [street, dataMoreInfo.numeroVia, dataMoreInfo.codigoPostal, description];
    addressList = addressList.filter(address => address.trim() !== '');
    return addressList.join(', ');
  }

  updateState(
    state: Partial<AppState>,
    formGroup: FormGroup,
    thereIsSubSpeciality: boolean): Partial<AppState> {
    const speciality: Speciality = formGroup?.value?.speciality;
    const subSpeciality: Subespecialidade = formGroup?.value?.subSpeciality;
    const medicalGuide: MedicalGuide = formGroup?.value?.medicalGuide;
    const searchParams = state.searchParams || {};
    searchParams.speciality = speciality;
    searchParams.subSpeciality = subSpeciality;
    searchParams.medicalGuide = medicalGuide;

    if (!thereIsSubSpeciality) {
      searchParams.subSpeciality = null;
    }

    return { ...state, searchParams };
  }

  requestMedicalAppointment(
    center: Partial<MedicalCenter>,
    state: Partial<AppState>): void {
    const url = this.url.getIdentifyUserUrls();


    this.utils.openExternalUrl(url, false);
  }

}
